import type { CorridorDefinition } from "@/types/corridor";

import heroVideo from "@/assets/plain.mp4";

const CITY_A = "Hyderabad";
const CITY_B = "Vijayawada";

const stats = {
  distanceKm: 275,
  driveTime: "4.5–5.5 hrs",
  highway: "NH65",
  zonesA: ["Hitech City", "Gachibowli", "LB Nagar", "Uppal", "Miyapur"],
  zonesB: ["Benz Circle", "Auto Nagar", "Kanaka Durga Temple", "PVP Mall", "Eluru Road"],
  busFare: 750,
  cabFare: 4500,
  carpoolFare: 499,
  monthlySavings: 3000,
  ridesToday: 17,
  completedRides: 1240,
  avgRating: 4.8,
  travellers: 2980,
};

const faqs: CorridorDefinition["faqs"] = [
  {
    q: `Is carpool safe between ${CITY_A} and ${CITY_B}?`,
    a: "Yes. Every Duri host and guest is ID-verified with ratings, vehicle details and reviews visible before you book. In-app chat, live trip tracking and a 24x7 SOS option keep your ride secure — in both directions.",
  },
  {
    q: `How much can I save carpooling between ${CITY_A} and ${CITY_B}?`,
    a: `Shared rides start at ₹${stats.carpoolFare} per seat — about 35% cheaper than AC buses (~₹${stats.busFare}) and up to 80% cheaper than a private cab (~₹${stats.cabFare}). Daily two-way commuters save up to ₹${stats.monthlySavings.toLocaleString()}/month.`,
  },
  {
    q: `How do I find a ride from ${CITY_A} to ${CITY_B} or back?`,
    a: `Open Duri, pick your direction (${CITY_A} → ${CITY_B} or ${CITY_B} → ${CITY_A}), enter pickup, drop and date. Browse verified hosts and confirm a seat in under 60 seconds.`,
  },
  {
    q: "Are return rides available the same day?",
    a: `Yes. Both ${CITY_A} → ${CITY_B} and ${CITY_B} → ${CITY_A} have daily morning and evening departures. Most weekend travellers book a round trip in one go.`,
  },
  {
    q: "Can women travel safely on Duri carpools?",
    a: "Absolutely. Women guests can filter for women-only or women-preferred rides, see verified profiles, and share live trip status with family directly from the app.",
  },
  {
    q: `How long does the ${CITY_A}–${CITY_B} drive take?`,
    a: `About ${stats.driveTime} via ${stats.highway} covering ${stats.distanceKm} km in either direction, including one short tea/snack break at Suryapet or Kodad.`,
  },
];

export const hyderabadVijayawadaCorridor: CorridorDefinition = {
  slug: "hyderabad-vijayawada",
  cityA: CITY_A,
  cityB: CITY_B,
  stats,
  heroImageSrc: "/Chalo-uploads/ed3e4cb1-8d78-4271-9f34-a0f4c209dca5.png",
  heroImageAlt: "Iconic illuminated VIJAYAWADA monument at night — gateway to Andhra Pradesh",
  heroVideoSrc: heroVideo,
  seoOgImage:
    "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/64ab5ad3-4f6e-457e-94c1-24567a339f8d/id-preview-1268a46e--a6ccb7a0-9a40-44ba-99d3-be21b3278db5.lovable.app-1774604645516.png",
  midCta: {
    title: "Don't drive alone on NH65",
    subtitle:
      "List your empty seats and recover up to ₹2,000 per trip — or grab a verified seat in seconds.",
  },
  localLife: {
    eyebrow: "Along the corridor",
    title: "Local life & weekend escapes",
    intro: `Don't just travel — discover. From the riverside calm of the Krishna to the ancient caves of Undavalli, every ${CITY_A}–${CITY_B} ride is a chance to slow down at the right spot.`,
  },
  problemSolution: {
    headline: `Tired of the same old ${CITY_A}–${CITY_B} commute?`,
    sub: "Buses are slow, cabs are pricey, and driving alone is wasteful. Duri fixes all three.",
  },
  spots: [
    {
      icon: "waves",
      title: "Prakasam Barrage",
      meta: "Vijayawada",
      tag: "Sunset walk",
      text: "A 1.2 km dam stretch over the Krishna — best at golden hour with street chai stalls lining the promenade.",
      featured: true,
    },
    {
      icon: "mountain",
      title: "Undavalli Caves",
      meta: "6 km from Vijayawada",
      tag: "Heritage · 5th century",
      text: "Rock-cut caves featuring a monolithic Vishnu — a quiet hour of history before you head into town.",
    },
    {
      icon: "treePalm",
      title: "Bhavani Island",
      meta: "Krishna river",
      tag: "Day trip",
      text: "One of India's largest river islands. Boating, zip-lines and picnic lawns — a 30-min auto from Benz Circle.",
    },
    {
      icon: "landmark",
      title: "Nagarjuna Sagar",
      meta: "Detour from NH65",
      tag: "Dam & Buddhist relics",
      text: "Worth a half-day stop — the world's tallest masonry dam and the serene Nagarjunakonda island museum.",
    },
    {
      icon: "utensils",
      title: "Andhra meals on the way",
      meta: "Suryapet · Kodad",
      tag: "Highway dhabas",
      text: "Gongura mutton, natu kodi pulusu and bottomless ghee rice — the NH65 dhabas are a meal in themselves.",
    },
    {
      icon: "building2",
      title: "Old City, Hyderabad",
      meta: "Charminar quarter",
      tag: "Biryani · Night markets",
      text: "End your ride with Hyderabadi biryani, Irani chai at Nimrah and lac bangles at Laad Bazaar.",
    },
  ],
  happenings: [
    {
      when: "Oct – Feb",
      title: "Kanaka Durga Temple darshan",
      where: CITY_B,
      text: "Cool months bring peak pilgrimage traffic — book seats early on Friday & Saturday nights.",
    },
    {
      when: "Year-round weekends",
      title: "Bhavani Island getaways",
      where: `${CITY_B} riverfront`,
      text: "Boat rides, picnic spots and zip-lines on the Krishna — a 30-min auto from Benz Circle.",
    },
    {
      when: "Jan & Feb",
      title: "Numaish exhibition",
      where: CITY_A,
      text: "India's longest consumer fair at Nampally — Vijayawada families ride up for the weekend.",
    },
  ],
  testimonials: [
    {
      name: "Pradeep K.",
      role: "IT pro, Hitech City",
      rating: 5,
      text: `I commute to Vijayawada every other weekend. Duri saves me ₹2,500 a month and I've made friends on the way!`,
    },
    {
      name: "Sneha R.",
      role: "Student, BITS Hyderabad",
      rating: 5,
      text: "Found a women-preferred ride home in 10 minutes. Driver was verified and super polite. Felt safer than the bus.",
    },
    {
      name: "Ravi M.",
      role: "Host, Tata Nexon",
      rating: 5,
      text: "I drive HYD-VJA twice a week anyway. Now my fuel is fully covered and the car never goes empty.",
    },
  ],
  faqs,
};
